import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type {
  ReportPreviewContentSection,
  ReportPreviewPayload,
  ReportPreviewPerformanceSection,
  ReportPreviewProfile,
} from 'api/analytics-reports/types';
import type {
  ContentPost,
  PerformancePage,
  PerformancePageTimeSeries,
} from 'api/new-analytics/types';
import { CHART_COLORS } from 'app/modules/analytics/components/shared/charts';
import type { PerformanceChartData } from 'app/modules/analytics/modules/performance/types/chartTypes';
import {
  buildMultiSeriesByPlatform,
  buildStackedBarDatasetsByPlatform,
} from 'app/modules/analytics/modules/performance/utils/buildGroupPerformanceChartData';
import {
  buildTimeAxis,
  collectSortedDates,
  valuesForDates,
} from 'app/modules/analytics/modules/performance/utils/timeSeriesHelpers';
import { getPerformanceChartConfig } from 'app/modules/analytics/modules/reports/utils/getPerformanceChartConfig';
import {
  FB_REACTIONS_ORDER,
  FB_REACTION_SVGS,
} from 'app/modules/analytics/modules/performance/components/fbReactionsConstants';
import { getContentPostPreviewImageUrl } from 'app/modules/analytics/modules/content/utils/getContentPostPreviewImageUrl';
import { getFacebookReactionsFromOverview } from 'app/modules/analytics/modules/performance/utils/getFacebookReactionsFromOverview';
import {
  getMetricDisplay,
  getPercentMetricDisplay,
} from 'app/modules/analytics/modules/performance/utils/getPerformanceMetricDisplay';
import { getReportContentMetricValue } from 'app/modules/analytics/modules/reports/modules/createReport/utils/getReportContentMetricValue';
import { formatReportMetricLabel } from 'app/modules/analytics/modules/reports/utils/formatReportMetricLabel';
import { formatReportPreviewDateRange } from 'app/modules/analytics/modules/reports/utils/formatReportPreviewDateRange';
import { resolvePreviewContentPosts } from 'app/modules/analytics/modules/reports/utils/resolvePreviewContentPosts';
import { resolvePreviewContentSummary } from 'app/modules/analytics/modules/reports/utils/resolvePreviewContentSummary';
import { resolvePreviewPerformanceOverview } from 'app/modules/analytics/modules/reports/utils/resolvePreviewPerformanceOverview';
import { resolveReportPreviewProfileHeaderMeta } from 'app/modules/analytics/modules/reports/utils/resolveReportPreviewProfileHeaderMeta';
import NumberUtil from 'utils/number';
import type { TFunction } from 'i18next';
import type { AnalyticsExportPageProfile } from './AnalyticsExportPageFrame';
import type { AnalyticsExportPostCard } from './AnalyticsExportPostsPage';
import type { AnalyticsExportMetricTile } from './AnalyticsExportSummaryPage';

/** Ratio metrics render as `8.4%`; everything else is a compacted count. */
const PERCENT_PERFORMANCE_METRICS = new Set(['engagement_rate']);

/** The card grid is sized for one row; more posts would overflow the sheet. */
const POST_CARDS_PER_PAGE = 3;

/**
 * Total followers is the only metric that moves from a tile onto its own chart
 * page; everything else, new followers included, stays a figure on the summary.
 */
const CHARTED_PERFORMANCE_METRICS = ['followers'];

/**
 * `useFacebookOverviewCharts` is the app's version of this, but it reads the
 * analytics filter store, which the public export route has no business
 * touching. The series maths is the same shared helpers.
 */
function buildPaidOrganicViewsData(
  timeSeries: PerformancePageTimeSeries | undefined,
  t: TFunction,
): PerformanceChartData {
  const paid = timeSeries?.video_views_paid;
  const organic = timeSeries?.video_views_organic;

  if (![paid, organic].some((series) => series?.length)) {
    return { labels: [], datasets: [] };
  }

  const dates = collectSortedDates([paid, organic]);
  if (dates.length === 0) {
    return { labels: [], datasets: [] };
  }

  return {
    ...buildTimeAxis(dates),
    datasets: [
      {
        label: t('analyticsPerformance.legend.paid'),
        data: valuesForDates(paid, dates),
        color: CHART_COLORS.defaultDark,
      },
      {
        label: t('analyticsPerformance.legend.organic'),
        data: valuesForDates(organic, dates),
        color: CHART_COLORS.compareLight,
      },
    ],
  };
}

function buildMetricChartData(
  pages: PerformancePage[],
  metricKey: string,
): PerformanceChartData {
  return getPerformanceChartConfig(metricKey)?.chartType === 'bar'
    ? buildStackedBarDatasetsByPlatform(pages, metricKey)
    : buildMultiSeriesByPlatform(pages, metricKey);
}

export type AnalyticsExportPage =
  | {
      kind: 'tiles';
      key: string;
      title: string;
      subtitle: string;
      profile: AnalyticsExportPageProfile;
      tiles: AnalyticsExportMetricTile[];
    }
  | {
      kind: 'chart';
      key: string;
      title: string;
      subtitle: string;
      profile: AnalyticsExportPageProfile;
      chartType: 'line' | 'bar';
      stacked?: boolean;
      data: PerformanceChartData;
      headlineLabel: string;
      headlineValue: string;
    }
  | {
      kind: 'posts';
      key: string;
      title: string;
      subtitle: string;
      profile: AnalyticsExportPageProfile;
      posts: AnalyticsExportPostCard[];
    };

function buildReactionTiles(
  overview: ReturnType<typeof resolvePreviewPerformanceOverview>,
  t: TFunction,
): AnalyticsExportMetricTile[] {
  const reactions = getFacebookReactionsFromOverview(overview);

  return FB_REACTIONS_ORDER.map((id) => ({
    key: id,
    icon: (
      <img
        src={FB_REACTION_SVGS[id]}
        alt={t(`analyticsPerformance.fbReactionLabels.${id}`)}
        className="tw-h-full tw-w-full tw-object-contain"
      />
    ),
    value: NumberUtil.compact(reactions[id].value),
    changePercent: reactions[id].changePercent,
  }));
}

function buildPostCards(
  posts: ContentPost[],
  t: TFunction,
): AnalyticsExportPostCard[] {
  return posts.slice(0, POST_CARDS_PER_PAGE).map((post, index) => {
    const views = post.metrics.views ?? post.metrics.video_views;
    const metricsLine = [
      views !== undefined
        ? `${NumberUtil.compact(views)} ${t(
            'analyticsContent.metrics.views',
          ).toLowerCase()}`
        : null,
      post.metrics.shares !== undefined
        ? `${NumberUtil.compact(post.metrics.shares)} ${t(
            'analyticsContent.metrics.shares',
          ).toLowerCase()}`
        : null,
      post.metrics.saved !== undefined
        ? `${NumberUtil.compact(post.metrics.saved)} ${t(
            'analyticsContent.metrics.saves',
          ).toLowerCase()}`
        : null,
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');

    return {
      key: `${post.post_id ?? post.entity_id ?? index}`,
      imageUrl: getContentPostPreviewImageUrl(post),
      formatLabel: post.post_format,
      headlineValue:
        views !== undefined ? NumberUtil.compact(views) : NumberUtil.compact(0),
      caption: post.content?.trim() || post.preview?.text?.trim() || '',
      metricsLine,
    };
  });
}

/**
 * Turns a report preview into the ordered list of sheets: every profile's
 * performance sheets, then every profile's content sheets, so a group reads as
 * one report rather than alternating per profile. Both the printable export
 * and the on-screen presentation render the same list.
 */
export function useAnalyticsExportPages(
  preview: ReportPreviewPayload,
): AnalyticsExportPage[] {
  const { t } = useTranslation();

  const periodLabel = useMemo(() => {
    const { from, to } = preview.report.period;

    return from && to
      ? formatReportPreviewDateRange(dayjs(from), dayjs(to))
      : null;
  }, [preview.report.period]);

  const pages = useMemo(() => {
    const subtitleFor = (key: string) =>
      periodLabel ? t(key, { range: periodLabel }) : preview.report.name;

    const buildProfilePages = (profile: ReportPreviewProfile) => {
      const meta = resolveReportPreviewProfileHeaderMeta(profile, t);
      const pageProfile: AnalyticsExportPageProfile = {
        name: meta.label,
        imageUrl: meta.logoSrc,
        pageType: meta.pageType,
      };

      const performanceSection = profile.sections.find(
        (section): section is ReportPreviewPerformanceSection =>
          section.kind === 'performance',
      );
      const contentSection = profile.sections.find(
        (section): section is ReportPreviewContentSection =>
          section.kind === 'content',
      );

      const performanceOverview = resolvePreviewPerformanceOverview(
        profile.type,
        performanceSection?.data,
      );
      const contentSummary = resolvePreviewContentSummary(contentSection?.data);
      const contentPosts = resolvePreviewContentPosts(contentSection?.data);

      const performanceMetrics = performanceSection?.metrics ?? [];
      const chartedMetrics = performanceMetrics.filter((metricKey) =>
        CHARTED_PERFORMANCE_METRICS.includes(metricKey),
      );

      const performanceTiles: AnalyticsExportMetricTile[] = performanceMetrics
        .filter((metricKey) => !chartedMetrics.includes(metricKey))
        .map((metricKey) => {
          const display = PERCENT_PERFORMANCE_METRICS.has(metricKey)
            ? getPercentMetricDisplay(performanceOverview, metricKey)
            : getMetricDisplay(performanceOverview, metricKey);

          return {
            key: metricKey,
            label: formatReportMetricLabel(metricKey, 'performance', t),
            value: display.value,
            changePercent: display.badge,
          };
        });

      const contentTiles: AnalyticsExportMetricTile[] = (
        contentSection?.metrics ?? []
      ).map((metricKey) => ({
        key: metricKey,
        label: formatReportMetricLabel(metricKey, 'content', t),
        value: getReportContentMetricValue(contentSummary, metricKey) ?? '—',
      }));

      const performanceSheets: AnalyticsExportPage[] = [];
      const contentSheets: AnalyticsExportPage[] = [];

      if (performanceTiles.length > 0) {
        performanceSheets.push({
          kind: 'tiles',
          key: `${profile.id}-performance`,
          title: t('analyticsExports.performanceSummary'),
          subtitle: subtitleFor('analyticsExports.keyResultsForPeriod'),
          profile: pageProfile,
          tiles: performanceTiles,
        });
      }

      // Reactions are a Facebook-only breakdown of the performance overview.
      if (performanceSection && profile.platform === 'facebook') {
        performanceSheets.push({
          kind: 'tiles',
          key: `${profile.id}-reactions`,
          title: t('analyticsPerformance.reactions'),
          subtitle: subtitleFor('analyticsExports.keyResultsForPeriod'),
          profile: pageProfile,
          tiles: buildReactionTiles(performanceOverview, t),
        });
      }

      const performancePages = performanceSection?.data?.pages ?? [];

      for (const metricKey of chartedMetrics) {
        const config = getPerformanceChartConfig(metricKey);

        if (!config) {
          continue;
        }

        performanceSheets.push({
          kind: 'chart',
          key: `${profile.id}-chart-${metricKey}`,
          title: t(config.titleKey),
          subtitle: subtitleFor('analyticsExports.keyResultsForPeriod'),
          profile: pageProfile,
          chartType: config.chartType,
          stacked: config.stacked,
          data: buildMetricChartData(performancePages, metricKey),
          headlineLabel: formatReportMetricLabel(metricKey, 'performance', t),
          headlineValue: getMetricDisplay(performanceOverview, metricKey).value,
        });
      }

      const paidOrganicData = buildPaidOrganicViewsData(
        performancePages[0]?.time_series,
        t,
      );

      if (paidOrganicData.labels.length > 0) {
        performanceSheets.push({
          kind: 'chart',
          key: `${profile.id}-paid-organic`,
          title: t('analyticsPerformance.paidVsOrganicViews'),
          subtitle: subtitleFor('analyticsExports.keyResultsForPeriod'),
          profile: pageProfile,
          chartType: 'line',
          data: paidOrganicData,
          headlineLabel: t('analyticsPerformance.metrics.videoViews'),
          headlineValue: getMetricDisplay(performanceOverview, 'video_views')
            .value,
        });
      }

      if (contentTiles.length > 0) {
        contentSheets.push({
          kind: 'tiles',
          key: `${profile.id}-content`,
          title: t('analyticsExports.contentSummary'),
          subtitle: subtitleFor('analyticsExports.keyContentMetricsForPeriod'),
          profile: pageProfile,
          tiles: contentTiles,
        });
      }

      if (contentPosts.length > 0) {
        contentSheets.push({
          kind: 'posts',
          key: `${profile.id}-posts`,
          title: t('analyticsExports.topPerformingContent'),
          subtitle: subtitleFor('analyticsExports.keyContentMetricsForPeriod'),
          profile: pageProfile,
          posts: buildPostCards(contentPosts, t),
        });
      }

      return { performance: performanceSheets, content: contentSheets };
    };

    const perProfile = [...preview.profiles]
      .sort((left, right) => left.order_column - right.order_column)
      .map(buildProfilePages);

    return [
      ...perProfile.flatMap((profilePages) => profilePages.performance),
      ...perProfile.flatMap((profilePages) => profilePages.content),
    ];
  }, [periodLabel, preview.profiles, preview.report.name, t]);

  return pages;
}
